import React, { useMemo } from 'react';
import ReleaseList from './ReleaseList';
import { Release, Status } from '@/types';
import { useAccessControl } from '@/hooks/useAccessControl';

interface VersioningSystemProps {
  clientId?: string;
  hideHeader?: boolean;
  releases?: Release[];
  isLoading?: boolean;
  addRelease?: (release: Omit<Release, 'id'>) => Promise<string | undefined>;
  updateRelease?: (release: Release) => Promise<string | undefined>;
  deleteRelease?: (id: string) => Promise<string | undefined>;
  fetchReleases?: () => Promise<void>;
}

const VersioningSystem: React.FC<VersioningSystemProps> = ({
  clientId,
  hideHeader = false,
  releases: externalReleases,
  isLoading: externalIsLoading,
  addRelease: externalAddRelease,
  updateRelease: externalUpdateRelease,
  deleteRelease: externalDeleteRelease,
  fetchReleases: externalFetchReleases
}) => {
  const {
    isLoggedIn,
    isAdmin,
    clientId: userClientId,
  } = useAccessControl();
  
  const releases = externalReleases || [];
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : false;
  const updateRelease = externalUpdateRelease;
  const deleteRelease = externalDeleteRelease;
  
  const effectiveClientId = clientId || userClientId;
  
  const canDeleteRelease = (release: Release): boolean => {
    if (!isLoggedIn) return false;
    if (isAdmin) return true;
    return release.ownerId === userClientId;
  };

  // Find the highest version number across ALL releases
  const highestVersionId = useMemo(() => {
    if (releases.length === 0) return null;

    return releases.reduce((highest, current) => {
      const currentMatch = current.version.match(/^v?(\d+)/);
      const highestMatch = highest.version.match(/^v?(\d+)/);
      
      const currentVersion = currentMatch ? parseInt(currentMatch[1], 10) : 0;
      const highestVersion = highestMatch ? parseInt(highestMatch[1], 10) : 0;
      
      return currentVersion > highestVersion ? current : highest;
    }).id;
  }, [releases]);

  // Filter releases by status
  const onHoldReleases = releases.filter(release => release.status === 'on hold');
  const notStartedReleases = releases.filter(release => release.status === 'not ready');
  const readyReleases = releases.filter(release => release.status === 'ready for release');
  const completedReleases = releases.filter(release => release.status === 'released');

  if (isLoading) {
    return (
      <div className="text-center py-10">
        <p>Loading releases...</p>
      </div>
    );
  }

  // Only show sections that have releases
  const sections = [
    {
      title: 'On Hold',
      releases: onHoldReleases,
      status: 'on hold',
      isCompleted: false
    },
    {
      title: 'Not Ready',
      releases: notStartedReleases,
      status: 'not ready',
      isCompleted: false
    },
    {
      title: 'Ready for Release',
      releases: readyReleases,
      status: 'ready for release',
      isCompleted: false
    },
    {
      title: 'Released',
      releases: completedReleases,
      status: 'released',
      isCompleted: true
    }
  ].filter(section => section.releases.length > 0);

  if (sections.length === 0) {
    return (
      <div className="text-center py-10 border rounded-md bg-gray-50">
        <p className="text-gray-500">No releases found.</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 -translate-x-1/2" />
      
      {/* Status sections */}
      <div className="space-y-12 relative">
        {sections.map((section, index) => (
          <div key={section.status} className="relative">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white px-4 py-2 rounded-full border text-gray-600 z-10">
                {section.title}
              </div>
            </div>
            <ReleaseList
              releases={section.releases}
              updateRelease={updateRelease}
              deleteRelease={deleteRelease}
              canEdit={isLoggedIn}
              canDeleteFn={canDeleteRelease}
              clientId={effectiveClientId}
              isCompletedReleases={section.isCompleted}
              statusGroup={section.status}
              expandedReleaseId={highestVersionId}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default VersioningSystem;
